/**
 * SourceLabelDialog Component
 *
 * Modal dialog for labeling a sending source with category and subtype.
 * Labeling triggers automatic remediation task generation.
 */

import { useState } from "react";
import {
  X,
  Tag,
  Shield,
  HelpCircle,
  AlertTriangle,
  Server,
  GitBranch,
  Forward,
  Search,
  CheckCircle2,
} from "lucide-react";
import type { Source } from "@/lib/api";
import { Modal } from "@/components/ui/Modal";

// Label categories with metadata
const LABEL_CATEGORIES = [
  {
    id: "authorized",
    name: "Authorized Sender",
    description: "A known legitimate sender for your organization",
    icon: CheckCircle2,
    color: "green",
  },
  {
    id: "customer_support",
    name: "Customer Support",
    description: "Helpdesk, ticketing, or support platform",
    icon: HelpCircle,
    color: "blue",
  },
  {
    id: "cicd",
    name: "CI/CD & DevOps",
    description: "Build notifications, security alerts, monitoring",
    icon: GitBranch,
    color: "purple",
  },
  {
    id: "forwarder",
    name: "Forwarder",
    description: "Mailing list or email forwarding service",
    icon: Forward,
    color: "slate",
  },
  {
    id: "unauthorized",
    name: "Unauthorized / Malicious",
    description: "Spoofing attempt or unknown bad actor",
    icon: AlertTriangle,
    color: "red",
  },
  {
    id: "investigating",
    name: "Investigating",
    description: "Unknown source that needs research",
    icon: Search,
    color: "amber",
  },
] as const;

// Subtypes for each category
const LABEL_SUBTYPES: Record<string, Array<{ id: string; name: string; description: string }>> = {
  authorized: [
    { id: "authorized_sender", name: "Generic Authorized", description: "General authorized sender" },
    { id: "transactional", name: "Transactional", description: "Password resets, receipts, confirmations" },
    { id: "marketing", name: "Marketing", description: "Newsletters, campaigns, promotions" },
    { id: "corporate", name: "Corporate", description: "Internal/employee email" },
    { id: "infrastructure", name: "Infrastructure", description: "AWS SES, Azure, cloud services" },
    { id: "sso_identity", name: "SSO/Identity", description: "Okta, Auth0, Azure AD" },
  ],
  customer_support: [
    { id: "helpdesk", name: "Helpdesk", description: "Zendesk, Freshdesk, Help Scout" },
    { id: "ticketing", name: "Ticketing", description: "Jira Service Desk, etc." },
    { id: "chat_support", name: "Chat Support", description: "Intercom, Drift, etc." },
  ],
  cicd: [
    { id: "build_notifications", name: "Build Notifications", description: "GitHub, GitLab, CircleCI" },
    { id: "security_alerts", name: "Security Alerts", description: "PagerDuty, Datadog, New Relic" },
    { id: "monitoring", name: "Monitoring", description: "Uptime and APM alerts" },
    { id: "deployment", name: "Deployment", description: "Deployment notifications" },
  ],
  forwarder: [
    { id: "mailing_list", name: "Mailing List", description: "Google Groups, Listserv" },
    { id: "trusted_forwarder", name: "Trusted Forwarder", description: "Known legitimate forwarder" },
    { id: "arc_forwarder", name: "ARC Forwarder", description: "ARC-enabled forwarding" },
    { id: "alias_forward", name: "Alias Forward", description: "Email alias forwarding" },
  ],
  unauthorized: [
    { id: "malicious", name: "Malicious", description: "Confirmed bad actor" },
    { id: "spoofing_attempt", name: "Spoofing Attempt", description: "Trying to impersonate" },
    { id: "unknown_sender", name: "Unknown Sender", description: "Unrecognized, likely unauthorized" },
  ],
  investigating: [
    { id: "unknown", name: "Unknown", description: "Needs investigation" },
    { id: "pending_verification", name: "Pending Verification", description: "Waiting for confirmation" },
    { id: "needs_research", name: "Needs Research", description: "Requires deeper analysis" },
  ],
};

interface SourceLabelDialogProps {
  source: Source;
  tier: string;
  onLabel: (category: string, subtype: string, notes?: string) => Promise<void>;
  onClose: () => void;
}

export function SourceLabelDialog({
  source,
  tier,
  onLabel,
  onClose,
}: SourceLabelDialogProps) {
  const [category, setCategory] = useState<string>("");
  const [subtype, setSubtype] = useState<string>("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const subtypes = category ? LABEL_SUBTYPES[category] || [] : [];

  const sourceDisplay =
    source.ptr_hostname || source.source_ip || source.identifier;
  const orgDisplay =
    source.org_name || source.provider_name || "Unknown organization";

  async function handleSubmit() {
    if (!category || !subtype) return;

    setLoading(true);
    setError(null);

    try {
      await onLabel(category, subtype, notes || undefined);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to label source");
    } finally {
      setLoading(false);
    }
  }

  const canApply = tier === "pro" || tier === "enterprise" || tier === "enterprise_plus";
  const canGenerateApex = tier === "pro" || tier === "enterprise" || tier === "enterprise_plus";

  return (
    <Modal open onOpenChange={(open) => !open && onClose()}>
      <Modal.Content size="xl" hideCloseButton>
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <Tag className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-800">
                Label Source
              </h2>
              <p className="text-sm text-slate-500">
                Categorize this sender to generate remediation tasks
              </p>
            </div>
          </div>
          <Modal.Close>
            <button className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </Modal.Close>
        </div>

        {/* Source Info */}
        <div className="px-6 py-4 bg-slate-50 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <Server className="w-5 h-5 text-slate-400" />
            <div>
              <p className="font-medium text-slate-800">{sourceDisplay}</p>
              <p className="text-sm text-slate-500">{orgDisplay}</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Error message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* Category Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-slate-700 mb-3">
              What type of sender is this?
            </label>
            <div className="grid grid-cols-2 gap-3">
              {LABEL_CATEGORIES.map((cat) => {
                const Icon = cat.icon;
                const isSelected = category === cat.id;
                const colorClasses = {
                  green: isSelected
                    ? "border-emerald-500 bg-emerald-50"
                    : "hover:border-emerald-200",
                  blue: isSelected
                    ? "border-blue-500 bg-blue-50"
                    : "hover:border-blue-200",
                  purple: isSelected
                    ? "border-purple-500 bg-purple-50"
                    : "hover:border-purple-200",
                  slate: isSelected
                    ? "border-slate-500 bg-slate-50"
                    : "hover:border-slate-300",
                  red: isSelected
                    ? "border-red-600 bg-red-50"
                    : "hover:border-red-200",
                  amber: isSelected
                    ? "border-amber-500 bg-amber-50"
                    : "hover:border-amber-300",
                };

                return (
                  <button
                    key={cat.id}
                    onClick={() => {
                      setCategory(cat.id);
                      setSubtype("");
                    }}
                    className={`p-3 rounded-lg border-2 text-left transition-all ${
                      colorClasses[cat.color]
                    } ${isSelected ? "" : "border-slate-200"}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Icon
                        className={`w-4 h-4 ${
                          isSelected ? "text-slate-700" : "text-slate-400"
                        }`}
                      />
                      <span className="font-medium text-slate-800">
                        {cat.name}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500">{cat.description}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Subtype Selection */}
          {category && subtypes.length > 0 && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-700 mb-3">
                More specifically:
              </label>
              <div className="space-y-2">
                {subtypes.map((st) => (
                  <button
                    key={st.id}
                    onClick={() => setSubtype(st.id)}
                    className={`w-full p-3 rounded-lg border text-left transition-all ${
                      subtype === st.id
                        ? "border-amber-500 bg-amber-50"
                        : "border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="font-medium text-slate-800">
                          {st.name}
                        </span>
                        <p className="text-sm text-slate-500">
                          {st.description}
                        </p>
                      </div>
                      {subtype === st.id && (
                        <CheckCircle2 className="w-5 h-5 text-amber-500" />
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Notes */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Notes (optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any additional context about this source..."
              className="w-full p-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none"
              rows={3}
            />
          </div>

          {/* What happens next */}
          {category && subtype && (
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
              <p className="font-medium text-slate-800 mb-2 flex items-center gap-2">
                <Shield className="w-4 h-4 text-amber-500" />
                What happens next
              </p>
              <ul className="space-y-1.5 text-sm text-slate-600">
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-amber-500 rounded-full" />
                  AI will analyze the source and generate smart remediation tasks
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-amber-500 rounded-full" />
                  Tasks appear automatically when ready (usually a few seconds)
                </li>
                {canApply ? (
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full" />
                    One-click apply available for supported tasks
                  </li>
                ) : (
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-slate-400 rounded-full" />
                    You'll get step-by-step manual instructions
                  </li>
                )}
                {canGenerateApex ? (
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full" />
                    Generate APEX links to verify vendor setup
                  </li>
                ) : (
                  <li className="flex items-center gap-2 text-slate-400">
                    <div className="w-1.5 h-1.5 bg-slate-300 rounded-full" />
                    APEX links available on Pro plan
                  </li>
                )}
              </ul>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-200">
          <div className="flex items-center justify-end gap-3">
            <button
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors font-medium disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={!category || !subtype || loading}
              className="px-4 py-2 bg-amber-500 text-slate-900 rounded-lg font-medium hover:bg-amber-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 min-w-[140px] justify-center"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-slate-900/30 border-t-slate-900 rounded-full animate-spin" />
                  <span>Labeling...</span>
                </>
              ) : (
                <>
                  <Tag className="w-4 h-4" />
                  Label Source
                </>
              )}
            </button>
          </div>
        </div>
      </Modal.Content>
    </Modal>
  );
}